import { Comparison, Condition } from '@/shared/types/condition';

export type ConditionInputType =
  | 'text'
  | 'tag'
  | 'language'
  | 'timezone'
  | 'country'
  | 'agent'
  | 'integration'
  | 'business_hours'
  | 'channel'
  | 'status'
  | 'none';

export type ConditionAttributeConfig = {
  value: string;
  labelKey: string;
  input: ConditionInputType;
  comparisons: readonly Comparison[];
};

export type ConditionAttributeGroup = { labelKey: string; attributes: ConditionAttributeConfig[] };

const TEXT_COMPARISONS = [
  Comparison.IS,
  Comparison.IS_NOT,
  Comparison.STARTS_WITH,
  Comparison.ENDS_WITH,
  Comparison.CONTAINS,
  Comparison.NOT_CONTAINS,
  Comparison.UNKNOWN,
  Comparison.KNOWN,
];
const OPTION_COMPARISONS = [Comparison.IS, Comparison.IS_NOT, Comparison.UNKNOWN, Comparison.KNOWN];
const IS_COMPARISONS = [Comparison.IS, Comparison.IS_NOT];

export const EMPTY_CONDITION: Condition = { operator: 'and', conditions: [] };

// Views only: the API rejects shorter substring searches
export const SUBSTRING_MIN_LENGTH = 3;
export const SUBSTRING_COMPARISONS: readonly string[] = [
  Comparison.CONTAINS,
  Comparison.STARTS_WITH,
  Comparison.ENDS_WITH,
];

export const VALUELESS_COMPARISONS: readonly string[] = [Comparison.KNOWN, Comparison.UNKNOWN];

export const OPERATORS: Condition['operator'][] = ['and', 'or'];

export const OPERATOR_LABEL_KEYS: Record<Condition['operator'], string> = {
  and: 'rules.and',
  or: 'rules.or',
};

export const COMPARISON_LABEL_KEYS: Record<string, string> = {
  [Comparison.IS]: 'rules.eq',
  [Comparison.IS_NOT]: 'rules.ne',
  [Comparison.GREATER_THAN]: 'rules.gt',
  [Comparison.LESS_THAN]: 'rules.ls',
  [Comparison.IN]: 'rules.in',
  [Comparison.OUT]: 'rules.out',
  [Comparison.STARTS_WITH]: 'rules.starts_with',
  [Comparison.ENDS_WITH]: 'rules.ends_with',
  [Comparison.CONTAINS]: 'rules.contains',
  [Comparison.NOT_CONTAINS]: 'rules.not_contains',
  [Comparison.KNOWN]: 'rules.known',
  [Comparison.UNKNOWN]: 'rules.unknown',
};

export const STATUS_LABEL_KEYS: Record<string, string> = {
  open: 'rules.open',
  closed: 'rules.closed',
  missed: 'rules.missed',
};

const USER_ATTRIBUTES: ConditionAttributeConfig[] = [
  {
    value: 'user.display_name',
    labelKey: 'rules.name',
    input: 'text',
    comparisons: TEXT_COMPARISONS,
  },
  { value: 'user.email', labelKey: 'rules.email', input: 'text', comparisons: TEXT_COMPARISONS },
  { value: 'user.phone', labelKey: 'rules.phone', input: 'text', comparisons: TEXT_COMPARISONS },
  {
    value: 'user.language',
    labelKey: 'rules.language',
    input: 'language',
    comparisons: OPTION_COMPARISONS,
  },
  {
    value: 'user.timezone',
    labelKey: 'rules.timezone',
    input: 'timezone',
    comparisons: OPTION_COMPARISONS,
  },
  {
    value: 'user.location.country',
    labelKey: 'rules.country',
    input: 'country',
    comparisons: OPTION_COMPARISONS,
  },
  {
    value: 'user.location.city',
    labelKey: 'rules.city',
    input: 'text',
    comparisons: TEXT_COMPARISONS,
  },
  {
    value: 'user.browser',
    labelKey: 'rules.browser',
    input: 'text',
    comparisons: TEXT_COMPARISONS,
  },
  {
    value: 'user.platform',
    labelKey: 'rules.platform',
    input: 'text',
    comparisons: TEXT_COMPARISONS,
  },
];

const CHANNEL_ATTRIBUTE: ConditionAttributeConfig = {
  value: 'channel',
  labelKey: 'rules.from channel',
  input: 'channel',
  comparisons: IS_COMPARISONS,
};

const CONVERSATION_ATTRIBUTES: ConditionAttributeConfig[] = [
  CHANNEL_ATTRIBUTE,
  { value: 'session.tag', labelKey: 'rules.tag', input: 'tag', comparisons: IS_COMPARISONS },
  {
    value: 'context.global.client_url',
    labelKey: 'rules.customer url',
    input: 'text',
    comparisons: TEXT_COMPARISONS,
  },
  {
    value: 'context.global.ref',
    labelKey: 'rules.referral url',
    input: 'text',
    comparisons: TEXT_COMPARISONS,
  },
  {
    value: 'session.agent_id',
    labelKey: 'rules.agent assigned',
    input: 'agent',
    comparisons: OPTION_COMPARISONS,
  },
  {
    value: 'session.brain_id',
    labelKey: 'rules.ai agent assigned',
    input: 'none',
    comparisons: [Comparison.UNKNOWN, Comparison.KNOWN],
  },
];

const MESSAGE_ATTRIBUTE: ConditionAttributeConfig = {
  value: 'message.text',
  labelKey: 'rules.message content',
  input: 'text',
  comparisons: TEXT_COMPARISONS,
};

const STATUS_ATTRIBUTE: ConditionAttributeConfig = {
  value: 'status',
  labelKey: 'rules.status',
  input: 'status',
  comparisons: IS_COMPARISONS,
};

const BUSINESS_HOURS_ATTRIBUTE: ConditionAttributeConfig = {
  value: 'business_hours',
  labelKey: 'rules.business hours',
  input: 'business_hours',
  comparisons: [Comparison.IN, Comparison.OUT],
};

const INTEGRATION_ATTRIBUTE: ConditionAttributeConfig = {
  value: 'session.integration_id',
  labelKey: 'rules.integration',
  input: 'integration',
  comparisons: OPTION_COMPARISONS,
};

export const RULE_CONDITION_ATTRIBUTES: ConditionAttributeGroup[] = [
  { labelKey: 'rules.user', attributes: USER_ATTRIBUTES },
  { labelKey: 'rules.conversation', attributes: [...CONVERSATION_ATTRIBUTES, MESSAGE_ATTRIBUTE] },
  { labelKey: 'rules.availability', attributes: [BUSINESS_HOURS_ATTRIBUTE] },
];

export const VIEW_CONDITION_ATTRIBUTES: ConditionAttributeGroup[] = [
  { labelKey: 'rules.user', attributes: USER_ATTRIBUTES },
  { labelKey: 'rules.conversation', attributes: [...CONVERSATION_ATTRIBUTES, STATUS_ATTRIBUTE] },
];

export const ALL_CONDITION_ATTRIBUTES: ConditionAttributeConfig[] = [
  ...USER_ATTRIBUTES,
  ...CONVERSATION_ATTRIBUTES,
  MESSAGE_ATTRIBUTE,
  STATUS_ATTRIBUTE,
  BUSINESS_HOURS_ATTRIBUTE,
  INTEGRATION_ATTRIBUTE,
];
